import React from "react";
import { Box } from "@mui/material";
import { styled } from "@mui/material/styles";
import { motion } from "framer-motion";

export const Root = styled(Box)({
  display: "flex",
  position: "relative",
  alignItems: "center",
  justifyContent: "center",
  minHeight: "100vh",
  width: "100vw",
  overflow: "hidden",
  fontFamily: "'Fira Sans', sans-serif",
  background: "linear-gradient(135deg, #02357a 0%, #03407b 42%, #1474b7 72%, #0ea5e9 100%)",
  perspective: 1400,
});

export const BrandPanel = styled(Box)(({ theme }) => ({
  position: "absolute",
  inset: 0,
  display: "flex",
  flexDirection: "column",
  justifyContent: "center",
  alignItems: "center",
  padding: theme.spacing(8),
  overflow: "hidden",
  zIndex: 0,
  [theme.breakpoints.down("md")]: {
    justifyContent: "flex-start",
    padding: theme.spacing(5, 3),
  },
}));

export const FormPanel = styled(Box)(({ theme }) => ({
  position: "relative",
  zIndex: 2,
  flex: 1,
  display: "flex",
  flexDirection: "column",
  justifyContent: "center",
  alignItems: "center",
  minHeight: "100vh",
  width: "100vw",
  padding: theme.spacing(5),
  margin: "0 auto",
  background: "radial-gradient(circle at 50% 44%, rgba(255,255,255,0.12), transparent 30%)",
  [theme.breakpoints.down("md")]: {
    padding: theme.spacing(4, 3),
    background: "radial-gradient(circle at 50% 35%, rgba(255,255,255,0.14), transparent 38%)",
  },
}));

export const FormBox = styled(Box)(({ theme }) => ({
  position: "relative",
  overflow: "hidden",
  width: "100%",
  maxWidth: 570,
  padding: theme.spacing(5.5, 6),
  borderRadius: 34,
  background: "linear-gradient(145deg, rgba(255,255,255,0.24), rgba(255,255,255,0.09))",
  border: "1px solid rgba(255, 255, 255, 0.42)",
  boxShadow:
    "0 38px 110px rgba(2, 29, 71, 0.46), inset 0 1px 0 rgba(255,255,255,0.62), inset 0 -1px 0 rgba(255,255,255,0.14)",
  backdropFilter: "blur(30px) saturate(165%)",
  transformStyle: "preserve-3d",
  animation: "loginCardFloat 7s ease-in-out infinite",
  "&::before": {
    content: '""',
    position: "absolute",
    inset: "-45% auto -45% -90%",
    width: "72%",
    background:
      "linear-gradient(110deg, transparent 8%, rgba(255,255,255,0.18) 35%, rgba(255,255,255,0.64) 48%, rgba(255,255,255,0.18) 62%, transparent 86%)",
    transform: "rotate(12deg)",
    animation: "loginGlassSweep 5.8s ease-in-out infinite",
    pointerEvents: "none",
  },
  "&::after": {
    content: '""',
    position: "absolute",
    inset: 14,
    borderRadius: 26,
    border: "1px solid rgba(255,255,255,0.24)",
    boxShadow: "inset 0 0 38px rgba(255,255,255,0.08)",
    pointerEvents: "none",
  },
  "& > *": {
    position: "relative",
    zIndex: 1,
  },
  "@keyframes loginCardFloat": {
    "0%, 100%": { transform: "translate3d(0, 0, 0) rotateX(0deg) rotateY(0deg)" },
    "50%": { transform: "translate3d(0, -10px, 26px) rotateX(1.4deg) rotateY(-1.8deg)" },
  },
  "@keyframes loginGlassSweep": {
    "0%, 38%": { left: "-92%", opacity: 0 },
    "48%": { opacity: 1 },
    "72%, 100%": { left: "126%", opacity: 0 },
  },
  [theme.breakpoints.down("sm")]: {
    padding: theme.spacing(3),
    borderRadius: 20,
  },
}));

const Circle = styled(Box)(({ size, top, left, opacity = 0.12 }) => ({
  position: "absolute",
  width: size,
  height: size,
  top,
  left,
  borderRadius: "50%",
  background: `rgba(255,255,255,${opacity})`,
  pointerEvents: "none",
}));

const Ring = styled(Box)(({ size, top, left, borderWidth = 2 }) => ({
  position: "absolute",
  width: size,
  height: size,
  top,
  left,
  borderRadius: "50%",
  border: `${borderWidth}px solid rgba(255,255,255,0.2)`,
  pointerEvents: "none",
}));

const circleMotion = (x = 18, y = 16, duration = 9) => ({
  x: [0, x, -x * 0.35, 0],
  y: [0, -y, y * 0.45, 0],
  scale: [1, 1.04, 0.98, 1],
  transition: {
    duration,
    repeat: Infinity,
    repeatType: "mirror",
    ease: "easeInOut",
  },
});

export const panelVariants = {
  hidden: { opacity: 0, x: 40 },
  visible: { opacity: 1, x: 0, transition: { duration: 0.55, ease: [0.22, 1, 0.36, 1] } },
};

export const leftVariants = {
  hidden: { opacity: 0, x: -40 },
  visible: { opacity: 1, x: 0, transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] } },
};

export const itemVariants = {
  hidden: { opacity: 0, y: 16 },
  visible: (i) => ({
    opacity: 1,
    y: 0,
    transition: { delay: i * 0.08 + 0.2, duration: 0.45, ease: "easeOut" },
  }),
};

export default function AuthLayout({ brandContent, children }) {
  return (
    <Root>
      <BrandPanel component={motion.div} variants={leftVariants} initial="hidden" animate="visible">
        <Circle component={motion.div} animate={circleMotion(28, 20, 11)} size={480} top="-160px" left="-120px" opacity={0.11} />
        <Circle component={motion.div} animate={circleMotion(-22, 26, 10)} size={280} top="61%" left="58%" opacity={0.09} />
        <Circle component={motion.div} animate={circleMotion(16, -18, 8)} size={130} top="14%" left="72%" opacity={0.16} />
        <Ring component={motion.div} animate={{ rotate: [0, 360], scale: [1, 1.03, 1], transition: { duration: 22, repeat: Infinity, ease: "linear" } }} size={320} top="-72px" left="45%" />
        <Ring component={motion.div} animate={circleMotion(18, 12, 12)} size={190} top="70%" left="-42px" />
        <Ring component={motion.div} animate={circleMotion(-12, 10, 7)} size={86} top="42%" left="79%" borderWidth={1.5} />
        <Box sx={{ position: "absolute", bottom: 0, left: 0, right: 0, height: 120, background: "linear-gradient(to top, rgba(2, 53, 122, 0.38), transparent)", pointerEvents: "none" }} />
      </BrandPanel>

      <FormPanel component={motion.div} variants={panelVariants} initial="hidden" animate="visible">
        <FormBox>
          {brandContent && (
            <Box sx={{ textAlign: "center", mb: 4.5 }}>
              {brandContent}
            </Box>
          )}
          {children}
        </FormBox>
      </FormPanel>
    </Root>
  );
}
